'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

interface CountUpProps {
  value: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  animId?: string;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export default function CountUp({
  value,
  duration = 900,
  decimals = 0,
  prefix = '',
  suffix = '',
  className = '',
  animId,
}: CountUpProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef<HTMLSpanElement | null>(null);

  const formatter = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, [decimals]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduceMotion = prefersReducedMotion();

    if (reduceMotion) {
      setDisplayValue(value);
      return;
    }

    let rafId = 0;
    let startTime = 0;

    const run = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = easeOutCubic(progress);
      setDisplayValue(value * eased);

      if (progress < 1) {
        rafId = window.requestAnimationFrame(run);
      } else {
        setDisplayValue(value);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        rafId = window.requestAnimationFrame(run);
        observer.disconnect();
      },
      { threshold: 0.35, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [animId, decimals, duration, prefix, suffix, value]);

  const roundedValue = useMemo(() => {
    const factor = Math.pow(10, decimals);
    return Math.round(displayValue * factor) / factor;
  }, [decimals, displayValue]);

  const formatted = formatter.format(roundedValue);

  return (
    <span data-component-id="src/components/ui/CountUp.tsx:95:4" data-component-path="src/components/ui/CountUp.tsx" data-component-line="95" data-component-end-line="99" data-component-file="CountUp.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
